import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Check, ChevronDown, Lock, MapPin, ShoppingCart, Ticket } from 'lucide-react';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import currency from 'helpers/currency';
import {
    availableBatch,
    formatDay,
    groupSessionsByDay,
    isPresenceSession,
    kindLabel,
    passProducts,
    requiresPass,
    speakersOf,
    ticketProducts,
} from 'helpers/eventSessions';
import LoginModal from 'modules/onboarding/sign-in/LoginModal';
import { trackEvent } from 'scripts/pixels';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';

export const SCHEDULE_CART_ID = 'schedule-cart';
const PASS_SECTION_ID = 'schedule-pass';

const FILTERS = [
    { key: 'all', label: 'Todas' },
    { key: 'talks', label: 'Palestras' },
    { key: 'workshops', label: 'Minicursos' },
    { key: 'mine', label: 'Minhas sessões' },
];

const OK_TEXT = '#0a7a35';
// Estados em que a sessão já é da pessoa (comprada, no carrinho, presença marcada ou na lista).
const MINE = ['owned', 'selected', 'marked', 'reserved', 'listed'];
const NO_ACCESS = { ownedIds: [], presences: [], loading: false };
const NO_SEATS = {};

function priceOf(product) {
    const batch = availableBatch(product);
    const price = Number(batch?.price ?? product?.price);

    return price > 0 ? currency.format(price) : 'Grátis';
}

/** Por que um ingresso não pode ser adicionado agora, ou '' quando pode. */
function unavailableReason(product) {
    if (availableBatch(product)) {
        return '';
    }

    const upcoming = (product?.batches || []).some(batch => batch.upcoming);

    return upcoming ? 'Em breve' : 'Esgotado';
}

function goTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function SchedulePanel({
    event,
    selected = [],
    selectedSessions = [],
    onToggle,
    onToggleSession,
    onMark,
    onUnmark,
    isAuth = false,
    access = NO_ACCESS,
    seats = NO_SEATS,
    busy = null,
    error = '',
}) {
    const days = useMemo(() => groupSessionsByDay(event?.sessions), [event?.sessions]);
    const tickets = useMemo(() => ticketProducts(event), [event]);
    const passes = useMemo(() => passProducts(event), [event]);
    const owned = useMemo(() => new Set(access.ownedIds), [access.ownedIds]);
    const marked = useMemo(() => new Map((access.presences || []).map(item => [item.session_id, item.status])), [access.presences]);

    const [day, setDay] = useState(days[0]?.date);
    const [filter, setFilter] = useState('all');
    const [expanded, setExpanded] = useState({});
    const [chooser, setChooser] = useState(null);
    const [otherPasses, setOtherPasses] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);

    const needsPass = requiresPass(event);
    const checking = isAuth && access.loading;
    const holdsPass = needsPass && passes.some(pass => owned.has(pass.id));
    const passInCart = passes.some(pass => selected.includes(pass.id));
    const sessionsInCart = selectedSessions.length > 0 || selected.some(id => !passes.some(pass => pass.id === id));
    // Com passe à venda, as sessões abrem para quem já o tem aprovado ou o pôs no carrinho:
    // o passe e as sessões vão no mesmo pedido.
    const locked = needsPass && !holdsPass && !passInCart;

    function toggle(ticket) {
        if (!selected.includes(ticket.id)) {
            trackEvent('AddToCart', {
                value: Number(availableBatch(ticket)?.price) || 0,
                contentIds: [availableBatch(ticket)?.id],
                numItems: 1,
            });
        }

        onToggle(ticket.id);
    }

    /** Vagas que restam na sessão (null = sem limite); o número ao vivo vale mais que o da página. */
    function seatsLeft(session) {
        const live = seats[session.id];

        return live ? live.left : session.seats_left ?? null;
    }

    function ticketsFor(session) {
        return tickets.filter(ticket => session.product_ids?.includes(ticket.id));
    }

    /** O que a pessoa pode fazer agora com uma sessão de presença. */
    function presenceFor(session) {
        const status = marked.get(session.id);

        if (status === 'confirmed') return { kind: 'marked' };
        if (status === 'reserved') return { kind: 'reserved' };
        if (selectedSessions.includes(session.id)) return { kind: 'listed' };
        if (!needsPass) return { kind: 'none' };
        if (seatsLeft(session) === 0) return { kind: 'full' };

        return { kind: 'open', left: seatsLeft(session) };
    }

    /** O que a pessoa pode fazer com esta sessão agora. */
    function accessFor(session) {
        if (isPresenceSession(session)) {
            return presenceFor(session);
        }

        const options = ticketsFor(session);
        const bought = options.find(ticket => owned.has(ticket.id));

        if (bought) {
            return { kind: 'owned', ticket: bought };
        }

        const chosen = options.find(ticket => selected.includes(ticket.id));

        if (chosen) {
            return { kind: 'selected', ticket: chosen };
        }

        if (options.length === 0) {
            return { kind: 'none' };
        }

        const available = options.filter(ticket => !unavailableReason(ticket));

        if (available.length === 0) {
            return { kind: 'unavailable', reason: unavailableReason(options[0]) };
        }

        return { kind: 'sale', options: available };
    }

    /** Quem já escolheu uma opção vê "No carrinho"; para trocar, remove e escolhe a outra. */
    function choose(ticket) {
        toggle(ticket);
        setChooser(null);
    }

    const mine = new Set(
        days.flatMap(item => item.items)
            .filter(session => MINE.includes(accessFor(session).kind))
            .map(session => session.id)
    );
    const hasMine = mine.size > 0;
    const activeFilter = filter === 'mine' && !hasMine ? 'all' : filter;
    const current = days.find(item => item.date === day) || days[0];

    const sessions = (current?.items || []).filter(session => {
        if (activeFilter === 'talks') return session.kind !== 'workshop';
        if (activeFilter === 'workshops') return session.kind === 'workshop';
        if (activeFilter === 'mine') return mine.has(session.id);
        return true;
    });

    function renderPassCard(ticket) {
        const reason = unavailableReason(ticket);
        const isOwned = owned.has(ticket.id);
        const isSelected = selected.includes(ticket.id);

        let action = (
            <Cta type='button' onClick={() => toggle(ticket)}>
                Garantir ingresso
                <small>{priceOf(ticket)}</small>
            </Cta>
        );

        if (isOwned) {
            action = <Granted><Check size={16} strokeWidth={3} /> Ingresso garantido</Granted>;
        } else if (isSelected) {
            action = (
                <CtaDone type='button' aria-pressed onClick={() => toggle(ticket)}>
                    <Check size={16} strokeWidth={3} /> No carrinho
                    <small>Toque para remover</small>
                </CtaDone>
            );
        } else if (reason) {
            action = <Status>{reason}</Status>;
        }

        return (
            <PassCard key={ticket.id}>
                <PassInfo>
                    <b>{ticket.name}</b>
                    {ticket.description && <span>{ticket.description}</span>}
                </PassInfo>
                <PassAction>{action}</PassAction>
            </PassCard>
        );
    }

    /** O botão (ou o estado) de uma sessão em que a pessoa marca presença. */
    function renderPresence(session, state) {
        const working = busy === session.id;
        const left = seatsLeft(session);
        const seatsNote = left !== null && state.kind === 'open'
            ? <Small>{left === 1 ? 'Última vaga' : `${left} vagas restantes`}</Small>
            : null;

        if (state.kind === 'marked') {
            return (
                <>
                    <Granted><Check size={16} strokeWidth={3} /> Presença marcada</Granted>
                    <LinkButton type='button' disabled={working} onClick={() => onUnmark(session)}>
                        {working ? 'Desmarcando…' : 'Desmarcar presença'}
                    </LinkButton>
                </>
            );
        }

        if (state.kind === 'reserved') {
            return (
                <>
                    <Granted><Check size={16} strokeWidth={3} /> Presença reservada</Granted>
                    <Small>Confirma quando o pagamento for aprovado</Small>
                </>
            );
        }

        if (state.kind === 'listed') {
            return (
                <CtaDone type='button' aria-pressed onClick={() => onToggleSession(session.id)}>
                    <Check size={16} strokeWidth={3} /> Na sua lista
                    <small>Toque para remover</small>
                </CtaDone>
            );
        }

        if (state.kind === 'none') {
            return <Small>Sem ingresso do evento à venda</Small>;
        }

        if (state.kind === 'full') {
            return <Status>Esgotado</Status>;
        }

        if (locked) {
            return (
                <>
                    <CtaLocked type='button' onClick={() => goTo(PASS_SECTION_ID)}>
                        <Lock size={15} /> Marcar presença
                        <small>Antes, escolha o ingresso do evento</small>
                    </CtaLocked>
                    {seatsNote}
                </>
            );
        }

        // Com o ingresso já aprovado a presença é marcada na hora; com ele só no
        // carrinho, a sessão entra na lista e vai no mesmo pedido.
        const direct = holdsPass;

        return (
            <>
                <Cta
                    type='button'
                    disabled={working}
                    onClick={() => (direct ? onMark(session) : onToggleSession(session.id))}
                >
                    {working ? 'Marcando…' : 'Marcar presença'}
                    <small>Grátis</small>
                </Cta>
                {seatsNote}
            </>
        );
    }

    function renderAction(session) {
        const state = accessFor(session);

        if (isPresenceSession(session)) {
            return renderPresence(session, state);
        }

        const capacity = session.capacity && session.kind === 'workshop'
            ? <Capacity>{session.capacity} vagas</Capacity>
            : null;

        if (state.kind === 'owned') {
            return (
                <>
                    <Granted><Check size={16} strokeWidth={3} /> Acesso garantido</Granted>
                    <Small>{state.ticket.name}</Small>
                </>
            );
        }

        if (state.kind === 'none') {
            return <Small>Sem ingresso à venda</Small>;
        }

        if (state.kind === 'unavailable') {
            return <Status>{state.reason}</Status>;
        }

        if (state.kind === 'selected') {
            return (
                <>
                    <CtaDone type='button' aria-pressed onClick={() => toggle(state.ticket)}>
                        <Check size={16} strokeWidth={3} /> No carrinho
                        <small>Toque para remover</small>
                    </CtaDone>
                    {ticketsFor(session).length > 1 && <Small>{state.ticket.name}</Small>}
                </>
            );
        }

        if (locked) {
            return (
                <>
                    <CtaLocked type='button' onClick={() => goTo(PASS_SECTION_ID)}>
                        <Lock size={15} /> Comprar adicional
                        <small>Antes, escolha o ingresso do evento</small>
                    </CtaLocked>
                    {capacity}
                </>
            );
        }

        const [first] = state.options;

        if (state.options.length === 1) {
            return (
                <>
                    <Cta type='button' onClick={() => choose(first)}>
                        Comprar adicional
                        <small>{priceOf(first)}</small>
                    </Cta>
                    {capacity}
                </>
            );
        }

        const open = chooser === session.id;

        return (
            <>
                <Cta type='button' aria-expanded={open} onClick={() => setChooser(open ? null : session.id)}>
                    Comprar adicional
                    <small>{state.options.length} opções</small>
                </Cta>
                {open && (
                    <Choices>
                        {state.options.map(ticket => (
                            <Choice key={ticket.id} type='button' onClick={() => choose(ticket)}>
                                <span>{ticket.name}</span>
                                <b>{priceOf(ticket)}</b>
                            </Choice>
                        ))}
                    </Choices>
                )}
                {capacity}
            </>
        );
    }

    const cartCount = selected.length + selectedSessions.length;
    const ownedPasses = passes.filter(pass => owned.has(pass.id));
    const passesToBuy = passes.filter(pass => !owned.has(pass.id));

    return (
        <Wrapper>
            <Heading>Programação</Heading>

            {error && <ErrorNote role='alert'>{error}</ErrorNote>}

            {!needsPass && (
                <Lead>Escolha as sessões que quer assistir. Cada ingresso vale para uma pessoa e vai para o carrinho.</Lead>
            )}

            {needsPass && (
                <PassSection id={PASS_SECTION_ID} $done={holdsPass}>
                    {checking && <Lead>Verificando o seu ingresso…</Lead>}

                    {!checking && holdsPass && (
                        <>
                            <PassDone>
                                <Ticket size={22} />
                                <div>
                                    <b>Ingresso do evento garantido</b>
                                    <span>{ownedPasses.map(pass => pass.name).join(' · ')}. Agora marque presença nas sessões abaixo.</span>
                                </div>
                            </PassDone>

                            {passesToBuy.length > 0 && (
                                <>
                                    <MoreToggle
                                        type='button'
                                        aria-expanded={otherPasses}
                                        onClick={() => setOtherPasses(current => !current)}
                                    >
                                        Outros ingressos do evento
                                        <ChevronDown size={16} className={otherPasses ? 'open' : ''} />
                                    </MoreToggle>
                                    {otherPasses && passesToBuy.map(renderPassCard)}
                                </>
                            )}
                        </>
                    )}

                    {!checking && !holdsPass && (
                        <>
                            <StepTitle><StepNumber>1</StepNumber> Escolha o ingresso do evento</StepTitle>
                            <Lead>
                                Comece pelo ingresso do evento e depois marque presença nas palestras e nos minicursos
                                que quer assistir. Tudo vai para o mesmo carrinho e você paga uma vez só.
                            </Lead>

                            {passes.map(renderPassCard)}

                            {passInCart && (
                                <Hint>
                                    Ingresso no carrinho. Agora marque presença nas sessões abaixo e finalize tudo de uma vez.
                                    {sessionsInCart && ' Se tirar o ingresso do evento, as sessões escolhidas saem junto.'}
                                </Hint>
                            )}

                            {!isAuth && (
                                <Hint>
                                    Já tem o ingresso?{' '}
                                    <LinkButton type='button' onClick={() => setLoginOpen(true)}>
                                        Entre na sua conta
                                    </LinkButton>{' '}
                                    para liberar a programação.
                                </Hint>
                            )}
                        </>
                    )}
                </PassSection>
            )}

            {needsPass && (
                <StepTitle $muted={locked}>
                    <StepNumber $muted={locked}>{holdsPass ? <Check size={14} strokeWidth={3} /> : 2}</StepNumber>
                    Marque presença nas sessões
                    {locked && <LockNote><Lock size={13} /> libera quando você escolher o ingresso do evento</LockNote>}
                </StepTitle>
            )}

            <Days>
                {days.map(item => {
                    const { weekday, label } = formatDay(item.date);
                    const unlocked = item.items.some(session => mine.has(session.id));

                    return (
                        <DayButton
                            key={item.date}
                            type='button'
                            aria-pressed={current?.date === item.date}
                            $active={current?.date === item.date}
                            onClick={() => setDay(item.date)}
                        >
                            {unlocked && <Dot title='Tem sessão sua neste dia' />}
                            <small>{weekday}</small>
                            <b>{label}</b>
                            <em>{item.items.length} {item.items.length === 1 ? 'sessão' : 'sessões'}</em>
                        </DayButton>
                    );
                })}
            </Days>

            <Filters>
                {FILTERS.map(item => (
                    <Chip
                        key={item.key}
                        type='button'
                        aria-pressed={activeFilter === item.key}
                        $active={activeFilter === item.key}
                        disabled={item.key === 'mine' && !hasMine}
                        onClick={() => setFilter(item.key)}
                    >
                        {item.label}
                    </Chip>
                ))}
            </Filters>

            {sessions.length === 0 && (
                <Empty>Nenhuma sessão neste dia com esse filtro.</Empty>
            )}

            {sessions.map(session => {
                const state = accessFor(session);
                const hasCover = !!session.image_url;
                const isOpen = !!expanded[session.id];

                return (
                    <Session
                        key={session.id}
                        $included={MINE.includes(state.kind)}
                        $cover={hasCover}
                    >
                        <Side>
                            {hasCover && <Cover src={session.image_url} alt='' loading='lazy' />}
                            <Time>
                                <b>{session.start}</b>
                                {session.end && <span>até {session.end}</span>}
                            </Time>
                        </Side>

                        <Body>
                            <Meta>
                                <Kind $kind={session.kind}>{kindLabel(session)}</Kind>
                                {session.location && (
                                    <Place><MapPin size={13} /> {session.location}</Place>
                                )}
                            </Meta>
                            <SessionTitle>{session.title}</SessionTitle>

                            {speakersOf(session).length > 0 && (
                                <Speakers>
                                    {speakersOf(session).map(person => (
                                        <Speaker key={person.id ?? person.name}>
                                            <DynamicProfileImage size='28px' img={person.photo_url} word={person.name} />
                                            <span>{person.name}</span>
                                        </Speaker>
                                    ))}
                                </Speakers>
                            )}
                        </Body>

                        <Access $locked={locked}>{renderAction(session)}</Access>

                        {session.description && (
                            <More>
                                <MoreToggle
                                    type='button'
                                    aria-expanded={isOpen}
                                    aria-controls={`session-${session.id}-about`}
                                    onClick={() => setExpanded(current => ({ ...current, [session.id]: !current[session.id] }))}
                                >
                                    Sobre esta sessão
                                    <ChevronDown size={18} className={isOpen ? 'open' : ''} />
                                </MoreToggle>
                                {isOpen && (
                                    <Description id={`session-${session.id}-about`}>{session.description}</Description>
                                )}
                            </More>
                        )}
                    </Session>
                );
            })}

            {cartCount > 0 && (
                <CartBar>
                    <span>
                        {cartCount} {cartCount === 1 ? 'item' : 'itens'} no carrinho
                    </span>
                    <button type='button' onClick={() => goTo(SCHEDULE_CART_ID)}>
                        <ShoppingCart size={16} /> Ver carrinho
                    </button>
                </CartBar>
            )}

            {loginOpen && <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
`;

const Heading = styled.h2`
    margin: 0;
    font-size: ${fonts.sizes.title};
    font-weight: ${fonts.weight.bold};
    color: ${colors.occasionalPurple};
`;

const Lead = styled.p`
    margin: 0;
    font-size: ${fonts.sizes.default};
    line-height: 1.5;
    color: ${colors.night};
`;

const Hint = styled(Lead)`
    font-size: 13px;
`;

const LinkButton = styled.button`
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    font: inherit;
    font-weight: 600;
    text-decoration: underline;
    color: ${colors.occasionalPurple};

    &:disabled { opacity: 0.6; cursor: wait; }
`;

const ErrorNote = styled.p`
    margin: 0;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 13px;
    background: ${colors.danger}1a;
    color: ${colors.dangerDark};
`;

const PassSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    border-radius: 14px;
    scroll-margin-top: 80px;
    background: ${({ $done }) => ($done ? `${OK_TEXT}0d` : `${colors.occasionalPurple}0a`)};
    border: 1px solid ${({ $done }) => ($done ? `${OK_TEXT}55` : `${colors.occasionalPurple}33`)};
`;

const StepTitle = styled.h3`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: ${({ $muted }) => ($muted === undefined ? '0' : '6px 0 0')};
    font-size: ${fonts.sizes.highlight};
    font-weight: ${fonts.weight.bold};
    color: ${({ $muted }) => ($muted ? colors.night : colors.nightDark)};
`;

const StepNumber = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 13px;
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${({ $muted }) => ($muted ? colors.nightLight : colors.occasionalPurple)};
`;

const LockNote = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.night};
`;

const PassDone = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    color: ${OK_TEXT};

    svg { flex: none; }

    div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    b { font-size: ${fonts.sizes.highlight}; }
    span { font-size: 13px; color: ${colors.nightDark}; }
`;

const PassCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.occasionalGrey};

    @media screen and (min-width: ${breakpoints.sm}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const PassInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;

    b { font-size: ${fonts.sizes.highlight}; color: ${colors.nightDark}; }
    span { font-size: 13px; line-height: 1.4; color: ${colors.night}; white-space: pre-line; }
`;

const PassAction = styled.div`
    flex: none;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;

    @media screen and (min-width: ${breakpoints.sm}) {
        width: 210px;
    }
`;

const Days = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(72px, 1fr));
    gap: 8px;
`;

const Dot = styled.span`
    position: absolute;
    top: 6px;
    right: 6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${colors.successDark};
`;

const DayButton = styled.button`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    padding: 9px 6px;
    border-radius: 10px;
    cursor: pointer;
    font-family: inherit;
    border: 1px solid ${({ $active }) => ($active ? colors.occasionalPurple : colors.occasionalGrey)};
    background: ${({ $active }) => ($active ? colors.occasionalPurple : colors.neutralLight)};
    color: ${({ $active }) => ($active ? colors.neutralLight : colors.nightDark)};

    small {
        font-size: ${fonts.sizes.small};
        text-transform: uppercase;
        letter-spacing: 0.05em;
        opacity: 0.8;
    }

    b {
        font-size: 17px;
        font-variant-numeric: tabular-nums;
    }

    em {
        font-style: normal;
        font-size: ${fonts.sizes.mini};
        opacity: 0.8;
    }
`;

const Filters = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Chip = styled.button`
    border-radius: 999px;
    padding: 5px 14px;
    font-family: inherit;
    font-size: 13px;
    cursor: pointer;
    font-weight: ${({ $active }) => ($active ? 600 : 500)};
    border: 1px solid ${({ $active }) => ($active ? colors.occasionalPurple : colors.occasionalGrey)};
    background: ${({ $active }) => ($active ? `${colors.occasionalPurple}14` : colors.neutralLight)};
    color: ${({ $active }) => ($active ? colors.occasionalPurple : colors.nightDark)};

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const Empty = styled.p`
    margin: 0;
    color: ${colors.night};
`;

const Session = styled.article`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    padding: 16px;
    background: ${colors.neutralLight};
    border-radius: 14px;
    border: 1px solid ${({ $included }) => ($included ? OK_TEXT : colors.occasionalGrey)};
    box-shadow: ${({ $included }) => ($included ? `inset 4px 0 0 ${OK_TEXT}` : 'none')};

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: ${({ $cover }) => ($cover ? '148px' : '84px')} minmax(0, 1fr) 210px;
        column-gap: 18px;
        align-items: start;
    }
`;

/* Foto da sessão (quando tem) e, logo abaixo, o horário. */
const Side = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Cover = styled.img`
    display: block;
    width: 100%;
    max-height: 190px;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    border-radius: 10px;
    background: ${colors.neutralDark};
`;

const Time = styled.div`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
    color: ${colors.nightDark};

    b {
        font-size: 20px;
        line-height: 1.2;
        font-variant-numeric: tabular-nums;
    }

    span {
        font-size: ${fonts.sizes.small};
        color: ${colors.night};
        font-variant-numeric: tabular-nums;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: column;
        gap: 0;
    }
`;

const Body = styled.div`
    min-width: 0;
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    font-size: 13px;
    color: ${colors.night};
`;

const Kind = styled.span`
    border-radius: 6px;
    padding: 1px 8px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    background: ${({ $kind }) => ($kind === 'workshop' ? colors.warning : `${colors.occasionalPurple}14`)};
    color: ${({ $kind }) => ($kind === 'workshop' ? '#7a4a00' : colors.occasionalPurple)};
`;

const Place = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const SessionTitle = styled.h3`
    margin: 6px 0 0;
    font-size: 17px;
    line-height: 1.3;
    font-weight: ${fonts.weight.bold};
    color: ${colors.nightDark};
`;

const Speakers = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-top: 8px;
`;

const Speaker = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: ${fonts.sizes.default};
    color: ${colors.night};
`;

const Access = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    opacity: ${({ $locked }) => ($locked ? 0.85 : 1)};
`;

/* O botão único de cada sessão: grande, roxo e com o preço embaixo. */
const Cta = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    width: 100%;
    min-height: 52px;
    padding: 10px 16px;
    border: 0;
    border-radius: 12px;
    cursor: pointer;
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${colors.occasionalPurple};
    box-shadow: 0 6px 16px ${colors.occasionalPurple}44;
    transition: transform 0.12s ease, box-shadow 0.12s ease;

    small {
        font-size: 12px;
        font-weight: 500;
        opacity: 0.9;
    }

    &:disabled { opacity: 0.7; cursor: wait; transform: none; }
    &:hover { transform: translateY(-1px); box-shadow: 0 8px 20px ${colors.occasionalPurple}55; }
    &:active { transform: translateY(0); }
    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: 3px; }
`;

const CtaDone = styled(Cta)`
    flex-direction: row;
    flex-wrap: wrap;
    gap: 2px 6px;
    color: ${colors.occasionalPurple};
    background: ${colors.neutralLight};
    border: 2px solid ${colors.occasionalPurple};
    box-shadow: none;

    small { flex-basis: 100%; text-align: center; color: ${colors.night}; }

    &:hover { box-shadow: none; background: ${colors.occasionalPurple}0d; }
`;

const CtaLocked = styled(Cta)`
    color: ${colors.night};
    background: ${colors.neutralDark};
    border: 1px dashed ${colors.nightLight};
    box-shadow: none;

    small { color: ${colors.night}; }

    &:hover { box-shadow: none; transform: none; }
`;

const Granted = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 44px;
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 700;
    text-align: center;
    color: ${OK_TEXT};
    background: ${OK_TEXT}14;
`;

const Small = styled.span`
    font-size: ${fonts.sizes.small};
    line-height: 1.4;
    text-align: center;
    color: ${colors.night};
`;

const Choices = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Choice = styled.button`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 9px 12px;
    border-radius: 10px;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    font-size: 13px;
    color: ${colors.nightDark};
    background: ${colors.neutral};
    border: 1px solid ${colors.occasionalGrey};

    b { white-space: nowrap; font-variant-numeric: tabular-nums; }

    &:hover { border-color: ${colors.occasionalPurple}; }
`;

const Status = styled.span`
    align-self: center;
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    background: ${colors.danger}1a;
    color: ${colors.dangerDark};
`;

const Capacity = styled(Small)``;

/* Descrição em formato de pergunta frequente: só abre quando a pessoa quer ler. */
const More = styled.div`
    grid-column: 1 / -1;
    border-top: 1px solid ${colors.occasionalGrey};
    padding-top: 2px;
`;

const MoreToggle = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 10px 2px;
    border: 0;
    background: none;
    cursor: pointer;
    font-family: inherit;
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    text-align: left;
    color: ${colors.occasionalPurple};

    svg { flex: none; transition: transform 0.18s ease; }
    svg.open { transform: rotate(180deg); }

    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: 2px; border-radius: 6px; }
`;

const Description = styled.p`
    margin: 0 0 8px;
    font-size: ${fonts.sizes.default};
    line-height: 1.6;
    color: ${colors.nightDark};
    white-space: pre-line;
`;

/* Só no celular: o carrinho fica abaixo da programação, e esta barra leva até ele. */
const CartBar = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 50;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
    background: ${colors.neutralLight};
    border-top: 1px solid ${colors.occasionalGrey};
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};

    button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 9px 16px;
        border: 0;
        border-radius: 999px;
        cursor: pointer;
        font-family: inherit;
        font-weight: 600;
        background: ${colors.occasionalPurple};
        color: ${colors.neutralLight};
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;
